import { SelectDesignTokens } from '@primeng/themes/types/select';

export interface ICustomSelectButtonDesignTokens extends SelectDesignTokens {
  root: SelectDesignTokens['root'] & {
    fontSize: string;
    selectOpenColor: string;
    selectOpenBorderColor: string;
    selectOpenBackground: string;
    hoverBackground: string;
    height?: string;
    gap?: string;
    labelPaddingLeft?: string;
    dropdownPaddingRight?: string;
  };
}

/**
 * `p-special` — the opt-in form-field look.
 *
 * The tokens below draw a select the way the toolbars want it: a compact chip that fills with the
 * primary colour while its panel is open. Forms want the opposite — a white box with a soft grey
 * rule, which stays white and only brightens its border while it is open — so that look is a class
 * a field asks for rather than a second set of tokens fighting the first.
 *
 * Put it on both halves: `styleClass="p-special"` for the field, `panelStyleClass="p-special"` for
 * the panel, which hangs off `<body>` and inherits nothing from the field it belongs to.
 */

export const baseSelectToken: ICustomSelectButtonDesignTokens = {
  root: {
    background: 'transparent',
    color: '{primary.500}',
    borderColor: '{primary.100}',
    disabledBackground: '{gray.50}',
    disabledColor: '{gray.500}',
    hoverBorderColor: '{primary.100}',
    hoverBackground: '{neutral.500}',

    selectOpenColor: '{white}',
    selectOpenBackground: '{primary.500}',
    selectOpenBorderColor: '{primary.500}',
    fontSize: '12px',

    shadow: 'none',
    borderRadius: '4px',
    paddingX: '8px',
    paddingY: '4px',
    labelPaddingLeft: '8px',
    dropdownPaddingRight: '4px',
    focusRing: {
      color: '{primary.700}',
      shadow: 'none',
      offset: '1px',
    },
  },
  dropdown: {
    width: 'auto',
    color: 'inherit',
  },
  overlay: {
    borderRadius: '8px',
    shadow: '0 0.25rem 1.5rem 0 #00000033',
    borderColor: '{white}',
    background: '{white}',
    color: '{gray.950}',
  },
  list: {
    padding: '4px',
    gap: '4px',
  },
  option: {
    focusBackground: '{neutral.500}',
    selectedBackground: '{neutral.500}',
    selectedFocusBackground: '{neutral.500}',
    color: '{gray.950}',
    focusColor: '{gray.950}',
    selectedColor: '{gray.950}',
    selectedFocusColor: '{gray.950}',
    padding: '4px 12px',
    borderRadius: '4px',
  },
  css: ({ dt }: { dt: (token: string, val?: string) => string }) => `
      .p-select {
        font-size: ${dt('select.fontSize')};
        color: ${dt('select.color')};
        height: ${dt('select.height')};
        gap: ${dt('select.gap')};
      }

      .p-select:not(.p-select-open):hover {
        background-color: ${dt('select.hoverBackground')};
        border-color: ${dt('select.hoverBorderColor')} !important;
      }

      .p-select.p-select-open {
        color: ${dt('select.selectOpenColor')};
        background-color: ${dt('select.selectOpenBackground')};
        border-color: ${dt('select.selectOpenBorderColor')} !important;
      }
      .p-select.p-select-open .p-select-label {
        color: ${dt('select.selectOpenColor')};
      }
      .p-select-label {
        padding-left: ${dt('select.labelPaddingLeft')};
      }
      .p-select-dropdown {
        padding-left: 0;
        padding-right: ${dt('select.dropdownPaddingRight')};
        color: inherit !important;
      }

      /* --- p-special: the plain form field ------------------------------------------------- */

      .p-select.p-special {
        /* Aura leaves the row stretched, which drops the value to the top of a fixed-height field */
        align-items: center;
        gap: 0;
        font-size: 0.75rem;
        font-weight: 600;
        color: ${dt('gray.950')};
        background: ${dt('surface.0')};
        border-color: ${dt('other.gray.100')};
        border-radius: 0.5rem;
      }

      /* a field keeps its white box under the pointer, and while its panel is open */
      .p-select.p-special:not(.p-select-open):hover {
        background-color: ${dt('surface.0')};
        border-color: ${dt('other.gray.100')} !important;
      }

      .p-select.p-special.p-select-open {
        color: ${dt('gray.950')};
        background-color: ${dt('surface.0')};
        border-color: ${dt('surface.300')} !important;
      }

      .p-select.p-special.p-select-open .p-select-label {
        color: ${dt('gray.950')};
      }

      .p-select.p-special .p-select-label {
        padding: 0.625rem 1rem;
      }

      .p-select.p-special .p-select-dropdown {
        padding-right: 1rem;
        color: ${dt('gray.500')} !important;
      }

      .p-select-overlay.p-special {
        margin-top: 0.375rem;
        border-radius: 0.75rem;
      }

      .p-select-overlay.p-special .p-select-list {
        padding: 0.25rem;
        gap: 0.25rem;
      }

      .p-select-overlay.p-special .p-select-option {
        padding: 0.625rem 0.75rem;
        border-radius: 0.5rem;
        font-size: 0.75rem;
        font-weight: 600;
      }
    `,
};
